
import { type WorkerClient } from '@graphql-box/worker-client';
import { type BreadcrumbRouteRule, type CreateRouteRules } from '@next-box/breadcrumb';
import { startCase } from 'lodash-es';
import { excludeSearchParams } from '#components/Breadcrumb/helpers/excludeSearchParams.ts';
import { DiscoverTypeRoute, SectionType, type SectionTypeRoute } from '#enums/mediaTypes.ts';
import { mapDiscoverTypeRouteToDiscoverTypeLabel, mapSectionTypeRouteToSectionType } from '#helpers/enumMappers.ts';
import { get } from '#helpers/otherHelpers.ts';

const queries = {
  [SectionType.Episode]: "query GetEpisodeName(\n  $id: ID!\n) {\n  episode(id: $id) {\n    name\n  }\n}\n",
  [SectionType.Movie]: "query GetMovieTitle(\n  $id: ID!\n) {\n  movie(id: $id) {\n    title\n  }\n}\n",
  [SectionType.Person]: "query GetPersonName(\n  $id: ID!\n) {\n  person(id: $id) {\n    name\n  }\n}\n",
  [SectionType.Season]: "query GetSeasonName(\n  $id: ID!\n) {\n  season(id: $id) {\n    name\n  }\n}\n",
  [SectionType.Tv]: "query GetTvName(\n  $id: ID!\n) {\n  tv(id: $id) {\n    name\n  }\n}\n",
};

export type CreateRouteRulesOptions = {
  client: WorkerClient;
};

export const createRouteRules: CreateRouteRules<CreateRouteRulesOptions> = ({ client }) => {
  const detailsBreadcrumbRule: BreadcrumbRouteRule<{ id: string; type: SectionTypeRoute }> = {
    regex: String.raw`/details/(?<type>[^/]+)/(?<id>[^?]+)`,
    resolve: async ({ id, type }, existingItem) => {
      // type is the named capture group for the section type route
      const sectionType = mapSectionTypeRouteToSectionType(type);
      const query = queries[sectionType];

      try {
        const result = await client.query(query, { variables: { id } });
        let name: string;

        switch (sectionType) {
          case SectionType.Episode: {
            name = get(result.data, 'episode.name');
            break;
          }

          case SectionType.Movie: {
            name = get(result.data, 'movie.title');
            break;
          }

          case SectionType.Person: {
            name = get(result.data, 'person.name');
            break;
          }

          case SectionType.Season: {
            name = get(result.data, 'season.name');
            break;
          }

          case SectionType.Tv: {
            name = get(result.data, 'tv.name');
            break;
          }
        }

        return {
          ...existingItem,
          label: name,
        };
      } catch {
        return existingItem;
      }
    },
  };

  const searchBreadcrumbRule: BreadcrumbRouteRule<{ keyphrase?: string }> = {
    regex: String.raw`/search-results\?(?:[^&]*&)*keyphrase=(?<keyphrase>[^&]+)(?:&[^&]*)*`,
    resolve: ({ keyphrase }, existingItem) => {
      let label = 'Search results';

      if (keyphrase) {
        label += ` for "${startCase(keyphrase.replaceAll('+', ' '))}"`;
      }

      return {
        ...existingItem,
        label,
      };
    },
  };

  const discoverBreadcrumbRule: BreadcrumbRouteRule<{ type?: DiscoverTypeRoute }> = {
    regex: String.raw`/discover/(?<type>[^?]*)`,
    resolve: ({ type = DiscoverTypeRoute.Movies }, existingItem) => {
      return { ...existingItem, label: `Discover ${mapDiscoverTypeRouteToDiscoverTypeLabel(type)}` };
    },
  };

  const homeBreadcrumbRule: BreadcrumbRouteRule = {
    regex: String.raw`^/(?:\?.*)?$`,
    resolve: (_capture, existingItem) => ({ ...existingItem, label: 'Home' }),
  };

  const excludeSearchParamsRule: BreadcrumbRouteRule = {
    regex: String.raw`^.*\?[^\s#]+$`,
    resolve: (_capture, existingItem) => ({
      ...existingItem,
      href: excludeSearchParams(existingItem.href, ['previewId', 'previewType']),
    }),
  };

  return [
    detailsBreadcrumbRule,
    searchBreadcrumbRule,
    discoverBreadcrumbRule,
    homeBreadcrumbRule,
    excludeSearchParamsRule,
  ];
};
